import { call, delay, put, select } from 'redux-saga/effects';
import {
  fetchExcelActivityLogsFailure,
  fetchExcelActivityLogsSuccess,
} from '../actions';
import { ApiError } from 'store/api/types';
import { FetchExcelActivityLogsRequestAction } from '../types';
import { checkResponse } from 'store/utils';
import {
  GetExcelActivityLogsResponse,
  getExcelActivityLogs,
  ExcelActivityLog,
} from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData, ProfilePicture } from 'store/app/types';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import { ActivityLog } from 'store/vat/models';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchExcelActivityLogsSaga(
  action: FetchExcelActivityLogsRequestAction
) {
  try {
    let err: ApiError | null;
    const { organisationId } = action.payload;

    const response: GetExcelActivityLogsResponse = yield call(
      getExcelActivityLogs,
      organisationId
    );

    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    // Backend may return logs either as a plain array or wrapped (e.g. { activities: [...] }).
    const rawData: any = response.data;
    let activityLogs: ExcelActivityLog[] = [];
    if (Array.isArray(rawData)) {
      activityLogs = rawData as ExcelActivityLog[];
    } else if (rawData && Array.isArray(rawData.activities)) {
      activityLogs = rawData.activities as ExcelActivityLog[];
    }

    // Enrich user profile pictures (mirrors VAT activity logs saga).
    let pp: ProfilePicture[] = yield select(getProfilePictures);
    const requestedUserUuids = new Set<string>();
    const enrichedExcelLogs: ExcelActivityLog[] = [...activityLogs];

    for (let i = 0; i < activityLogs.length; i++) {
      const user = activityLogs[i].user;
      if (user && user.hasProfilePicture && !user.profilePicture) {
        const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
        if (ppIdx > -1) {
          const profilePicture = pp[ppIdx].profilePicture;
          enrichedExcelLogs[i].user = {
            ...enrichedExcelLogs[i].user,
            profilePicture,
          };
        } else if (!requestedUserUuids.has(user.userUuid)) {
          requestedUserUuids.add(user.userUuid);
          yield put(fetchProfilePictureRequest(user.userUuid));
          yield delay(PICTURE_DELAY_TIME);
          pp = yield select(getProfilePictures);
          const newIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
          if (newIdx > -1) {
            const profilePicture = pp[newIdx].profilePicture;
            enrichedExcelLogs[i].user = {
              ...enrichedExcelLogs[i].user,
              profilePicture,
            };
          }
        }
      }
    }

    // Map ExcelActivityLog shape into shared ActivityLog shape used across VAT/Organisation.
    const mappedActivityLogs: ActivityLog[] = enrichedExcelLogs.map((log) => {
      const user = log.user;
      const fullNameFromUser =
        user && (user.firstName || user.lastName)
          ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
          : undefined;

      return {
        actionAccountDetailsId: log.accountDetailsId,
        type: log.type,
        createdAt: log.createdAt,
        fullName: log.userFullName || fullNameFromUser || '',
        email: user?.email ?? '',
        details: log.note,
        userDto: user,
        // linkedPeriodId intentionally left undefined for Excel logs for now
      };
    });

    yield put(
      fetchExcelActivityLogsSuccess(organisationId, mappedActivityLogs)
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchExcelActivityLogsFailure());
  }
}

